import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import StoreNavbar from '@/components/feature/StoreNavbar';
import SectionLink from '@/components/feature/SectionLink';
import { useCart } from '@/context/CartContext';
import { useMetaPixel } from '@/context/MetaPixelContext';
import { useProducts } from '@/hooks/useProducts';
import { useInventory } from '@/hooks/useInventory';
import { fetchVariants, createCheckoutOrder, createProductionOrder, type VariantRef, type CheckoutResult } from '@/services/checkout';
import { sendOrderConfirmation, type OrderNotificationItem } from '@/services/notifications';
import { getCompanySettings, getStoreNotices, DEFAULT_STORE_NOTICES, PCP_OFFICIAL_WHATSAPP, type StoreNotices } from '@/services/finances';
import { useShippingConfig } from '@/hooks/useShippingConfig';
import { useActiveDiscounts } from '@/hooks/useActiveDiscounts';
import { getProductDiscountPercent, discountedPrice } from '@/services/discounts';
import { resolveOrderShippingCost, sumItemsWeight, classifyZone, SHIPPING_ZONE_LABELS, COLOMBIA_CITIES } from '@/services/shipping';
import { getEpaycoPublicConfig, loadEpaycoScript, isEpaycoLoaded, openEpaycoCheckout, getEpaycoWebhookUrl, getEpaycoResponseUrl, getEpaycoPayPageUrl, type EpaycoPublicConfig, type EpaycoPayPayload } from '@/services/gateways';
import { getSistecreditoPublicConfig, startSistecreditoCheckout, getSistecreditoReturnUrl, type SistecreditoPublicConfig } from '@/services/sistecredito';
import Modal from '@/components/base/Modal';
import { loadDeliveryInfo, saveDeliveryInfo } from '@/lib/deliveryInfo';
import { savePendingEpayco, loadPendingEpayco, clearPendingEpayco, savePendingSistecredito, loadPendingSistecredito, clearPendingSistecredito } from '@/lib/pendingPayment';
import { lookupOrder, isSistecreditoOrderApproved } from '@/services/orderTracking';

interface ResolvedItem {
  variantId: string;
  productId: string;
  sizeId: string;
  productName: string;
  sizeName: string;
  sizeRange: string;
  quantity: number;
  unitPrice: number;
  finalPrice: number;
  discount: number;
  available: number;
  subtotal: number;
  production: boolean;
  chargeShipping: boolean;
  weightKg: number;
}

type PaymentMethod = 'cod' | 'transfer' | 'epayco' | 'sistecredito';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, removeItem, removeItems, updateQuantity, clearCart, count } = useCart();
  const { track, trackPurchase } = useMetaPixel();
  const { data: products, loading: productsLoading, error: productsError, refresh: refreshProducts } = useProducts();
  const {
    inventory,
    sizes,
    loading: inventoryLoading,
    error: inventoryError,
    refetch: refetchInventory,
  } = useInventory();
  const shippingConfig = useShippingConfig();
  const discounts = useActiveDiscounts();
  const [variants, setVariants] = useState<VariantRef[]>([]);
  const [contactWhatsapp, setContactWhatsapp] = useState(PCP_OFFICIAL_WHATSAPP);
  const [epaycoEnabled, setEpaycoEnabled] = useState(false);
  const [epaycoConfig, setEpaycoConfig] = useState<EpaycoPublicConfig | null>(null);
  const [sistecreditoConfig, setSistecreditoConfig] = useState<SistecreditoPublicConfig | null>(null);
  const [sistecreditoLoading, setSistecreditoLoading] = useState(false);
  const [sistecreditoError, setSistecreditoError] = useState('');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [department, setDepartment] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [confirmedMethod, setConfirmedMethod] = useState<PaymentMethod | null>(null);
  const [enableCod, setEnableCod] = useState(true);
  const [enableTransfer, setEnableTransfer] = useState(true);
  const [codSurchargePercent, setCodSurchargePercent] = useState(3);
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [onlineError, setOnlineError] = useState('');
  const [externalPaid, setExternalPaid] = useState('');
  const [sistecreditoApproved, setSistecreditoApproved] = useState('');

  // Aviso informativo del checkout (configurable desde el Panel Admin).
  const [notices, setNotices] = useState<StoreNotices>(DEFAULT_STORE_NOTICES);
  const [noticeAccepted, setNoticeAccepted] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [pendingRemove, setPendingRemove] = useState<ResolvedItem | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  const [results, setResults] = useState<CheckoutResult[]>([]);
  const initiateFiredRef = useRef(false);
  const paymentInfoFiredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    fetchVariants().then((v) => {
      if (!cancelled) setVariants(v);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getCompanySettings().then((settings) => {
      if (cancelled) return;
      if (settings.contactWhatsapp) {
        setContactWhatsapp(settings.contactWhatsapp);
      }
      setEnableCod(settings.enablePayOnDelivery);
      setEnableTransfer(settings.enableBankTransfer);
      setCodSurchargePercent(Number(settings.codSurchargePercent ?? 3));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getStoreNotices().then((n) => {
      if (!cancelled) setNotices(n);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getEpaycoPublicConfig().then((cfg) => {
      if (cancelled) return;
      setEpaycoConfig(cfg);
      setEpaycoEnabled(cfg.configured);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Configuración pública de Sistecrédito. La opción solo se ofrece cuando
  // `configured` (pasarela activada) es true; mientras el enrolamiento siga
  // pendiente, la opción queda OCULTA por completo (no se muestra ningún aviso).
  useEffect(() => {
    let cancelled = false;
    getSistecreditoPublicConfig().then((cfg) => {
      if (!cancelled) setSistecreditoConfig(cfg);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Precargamos el script oficial de ePayco para que, al pulsar "Pagar", el
  // navegador abra el checkout como parte del clic del usuario (y no lo bloquee
  // como ventana emergente).
  useEffect(() => {
    if (!epaycoEnabled) return;
    void loadEpaycoScript().catch(() => {});
  }, [epaycoEnabled]);

  // Escucha el resultado del pago cuando ePayco se abrió en una ventana externa
  // (modo 'popup'): la página de confirmación nos avisa por postMessage.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; status?: string; orderNumber?: string } | null;
      if (data?.type === 'epayco-pay-done' && data.status === 'APPROVED') {
        setExternalPaid(data.orderNumber ?? '');
        // El pago quedó aprobado: ahora sí vaciamos del carrito SOLO los productos
        // que formaban parte de este pedido (lo demás que se agregó se conserva).
        const pending = loadPendingEpayco();
        if (pending?.orderedKeys?.length) {
          removeItems(pending.orderedKeys);
        } else {
          clearCart();
        }
        clearPendingEpayco();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Si el cliente salió de la pasarela sin completar el pago y vuelve al
  // checkout, reconstruimos la pantalla de pago para el MISMO pedido pendiente
  // (sin crear una orden nueva y sin depender del carrito, que ya se vació al
  // registrar la orden). Cubre tanto ePayco como Sistecrédito. Se retoma el
  // intento más reciente; ambos comparten el mismo formato de pantalla.
  useEffect(() => {
    const pendingE = loadPendingEpayco();
    const pendingS = loadPendingSistecredito();
    const eAt = pendingE ? new Date(pendingE.savedAt).getTime() : -1;
    const sAt = pendingS ? new Date(pendingS.savedAt).getTime() : -1;

    if (pendingS && sAt >= eAt) {
      setResults(pendingS.orders && pendingS.orders.length > 0 ? pendingS.orders : [pendingS.order]);
      setConfirmedMethod('sistecredito');
      // Verificamos si el financiamiento YA quedó aprobado: solo entonces
      // vaciamos del carrito los productos de este pedido y descartamos el
      // intento. Si sigue pendiente, lo conservamos para retomar el MISMO
      // pedido sin duplicar; si fue rechazado, dejamos el carrito para reintentar.
      void (async () => {
        try {
          const res = await lookupOrder(pendingS.order.orderNumber, pendingS.contact);
          if (!res.found || !res.order) return;
          if (isSistecreditoOrderApproved(res.order)) {
            if (pendingS.orderedKeys?.length) removeItems(pendingS.orderedKeys);
            else clearCart();
            clearPendingSistecredito();
            setSistecreditoApproved(pendingS.order.orderNumber);
          } else if (res.order.financingDeclined || res.order.isCancelled) {
            clearPendingSistecredito();
          }
        } catch {
          // sin conexión: se sincronizará luego desde la tienda
        }
      })();
      return;
    }

    if (pendingE) {
      setResults(pendingE.orders && pendingE.orders.length > 0 ? pendingE.orders : [pendingE.order]);
      setConfirmedMethod('epayco');
    }
  }, []);

  // Autocompleta los datos de entrega con los del último pedido confirmado.
  useEffect(() => {
    const saved = loadDeliveryInfo();
    if (!saved) return;
    if (saved.name) setName(saved.name);
    if (saved.phone) setPhone(saved.phone);
    if (saved.email) setEmail(saved.email);
    if (saved.documentId) setDocumentId(saved.documentId);
    if (saved.address) setAddress(saved.address);
    if (saved.city) setCity(saved.city);
    if (saved.department) setDepartment(saved.department);
    setPrefilled(true);
  }, []);

  const resolved = useMemo<ResolvedItem[]>(() => {
    return items.map((item) => {
      const product = products.find((p) => p.id === item.productId);
      const variant = variants.find(
        (v) => v.product_id === item.productId && v.size_id === item.sizeId,
      );
      const size = sizes.find((s) => s.id === item.sizeId);
      const inv = inventory.find(
        (i) => i.productId === item.productId && i.sizeId === item.sizeId,
      );
      const available = inv ? inv.quantityAvailable : 0;
      const unitPrice = product?.commercialPrice ?? 0;
      const pct = getProductDiscountPercent(item.productId, discounts, product?.lineType);
      const finalPrice = discountedPrice(unitPrice, pct);
      // Solo la línea Premium puede ir a producción (encargo a fabricación).
      const isPremiumLine = (product?.lineType ?? 'premium') === 'premium';
      const production = isPremiumLine && (item.production === true || available <= 0);
      return {
        variantId: variant?.id ?? '',
        productId: item.productId,
        sizeId: item.sizeId,
        productName: product?.name ?? 'Producto',
        sizeName: size?.name ?? '',
        sizeRange: size?.sizeRange ?? '',
        quantity: item.quantity,
        unitPrice,
        finalPrice,
        discount: (unitPrice - finalPrice) * item.quantity,
        available,
        subtotal: finalPrice * item.quantity,
        production,
        chargeShipping: product?.chargeShipping ?? false,
        weightKg: size?.weightKg ?? 0.5,
      };
    });
  }, [items, products, variants, sizes, inventory, discounts]);

  const inStockItems = resolved.filter((r) => !r.production);
  const productionItems = resolved.filter((r) => r.production);
  const discountTotal = resolved.reduce((sum, r) => sum + r.discount, 0);
  const total = resolved.reduce((sum, r) => sum + r.subtotal, 0);
  const cartChargesShipping = resolved.some((r) => r.chargeShipping);
  // Valor de las babuchas sencillas (las que sí cobran envío). El recargo
  // contraentrega solo se calcula sobre estas + su flete; la premium, que tiene
  // envío gratis, no lo paga.
  const chargeableSubtotal = resolved
    .filter((r) => r.chargeShipping)
    .reduce((sum, r) => sum + r.subtotal, 0);
  // Peso que realmente cobra flete (solo productos de línea sencilla). El envío
  // de Interrapidísimo se calcula por peso y sector de la ciudad de destino.
  const chargeableWeight = sumItemsWeight(
    resolved.map((r) => ({ weightKg: r.weightKg, quantity: r.quantity, chargeShipping: r.chargeShipping })),
    true,
  );
  const shippingCost = resolveOrderShippingCost(city, cartChargesShipping, shippingConfig, chargeableWeight);
  const shippingZone = city ? classifyZone(city, shippingConfig) : null;
  // Recargo por pago contraentrega (configurable): % sobre el valor + el envío
  // de las babuchas sencillas. Solo aplica si el carrito tiene babuchas sencillas.
  const applyCodSurcharge = paymentMethod === 'cod' && codSurchargePercent > 0 && cartChargesShipping;
  const codSurcharge = applyCodSurcharge
    ? Math.round((chargeableSubtotal + shippingCost) * (codSurchargePercent / 100))
    : 0;
  const grandTotal = total + shippingCost + codSurcharge;
  const stockOk = inStockItems.every((r) => r.variantId && r.available >= r.quantity);

  // El resumen y el total dependen de los productos (precios) y del inventario
  // (stock). Si esas consultas aún cargan o fallan, NO se debe continuar: antes
  // el total quedaba en $0 sin aviso y solo aparecía al crear la orden.
  const dataLoading = productsLoading || inventoryLoading;
  const dataError = productsError || inventoryError;
  const dataReady = !dataLoading && !dataError;

  const retryData = () => {
    if (productsError) refreshProducts();
    if (inventoryError) refetchInventory();
  };

  // InitiateCheckout: se dispara una sola vez al entrar al checkout con carrito.
  useEffect(() => {
    if (initiateFiredRef.current || resolved.length === 0) return;
    initiateFiredRef.current = true;
    track('InitiateCheckout', {
      value: grandTotal,
      currency: 'COP',
      num_items: count,
      content_ids: resolved.map((r) => r.productId),
      contents: resolved.map((r) => ({
        id: r.productId,
        quantity: r.quantity,
        item_price: r.finalPrice,
      })),
    });
  }, [resolved, grandTotal, count, track]);

  // AddPaymentInfo: se dispara la primera vez que el cliente elige un método de
  // pago (mide el paso previo a la compra dentro del embudo).
  useEffect(() => {
    if (paymentInfoFiredRef.current || paymentMethod === null || resolved.length === 0) return;
    paymentInfoFiredRef.current = true;
    track('AddPaymentInfo', {
      value: grandTotal,
      currency: 'COP',
      num_items: count,
      payment_type: paymentMethod,
      content_ids: resolved.map((r) => r.productId),
      contents: resolved.map((r) => ({
        id: r.productId,
        quantity: r.quantity,
        item_price: r.finalPrice,
      })),
    });
  }, [paymentMethod, resolved, grandTotal, count, track]);

  const sistecreditoEnabled = Boolean(sistecreditoConfig?.configured);

  const formValid =
    name.trim() !== '' &&
    phone.trim() !== '' &&
    address.trim() !== '' &&
    city.trim() !== '' &&
    department.trim() !== '' &&
    paymentMethod !== null;

  // El aviso del checkout exige la casilla de confirmación antes de pagar.
  const noticeOk = !notices.checkoutEnabled || noticeAccepted;

  // Pedidos que se pueden pagar en línea juntos: un solo pedido, o un grupo
  // de compra combinada (normal + producción) que comparte `groupId`. Así el
  // pago en línea (ePayco / Sistecrédito) también funciona con carrito mixto.
  const payableOrders = useMemo(() => {
    if (results.length === 0) return null;
    if (results.length === 1) return results;
    const gid = results[0].groupId;
    if (gid && results.every((r) => r.groupId === gid)) return results;
    return null;
  }, [results]);
  const payableOrder = payableOrders ? payableOrders[0] : null;
  const payableTotal = payableOrders ? payableOrders.reduce((s, r) => s + r.total, 0) : 0;
  const payableOrderLabel = payableOrders ? payableOrders.map((r) => r.orderNumber).join(', ') : '';

  function splitName(fullName: string): { firstName: string; lastName: string } {
    const parts = fullName.trim().split(/\s+/);
    const firstName = parts[0] ?? '';
    const lastName = parts.slice(1).join(' ') || firstName;
    return { firstName, lastName };
  }

  function clearDeliveryInfo() {
    setName('');
    setPhone('');
    setEmail('');
    setDocumentId('');
    setAddress('');
    setCity('');
    setDepartment('');
    setPrefilled(false);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg('');

    if (!noticeOk) {
      setErrorMsg('Debes confirmar que entendiste la información importante del pedido para continuar.');
      return;
    }

    if (!dataReady) {
      setErrorMsg(
        dataError
          ? 'No pudimos cargar los precios y el stock. Revisa tu conexión e inténtalo de nuevo.'
          : 'Estamos cargando los precios y el stock. Espera un momento e inténtalo de nuevo.',
      );
      return;
    }

    if (!stockOk) {
      setErrorMsg('Uno o más productos no tienen stock suficiente. Revisa tu carrito.');
      return;
    }

    if (!paymentMethod) {
      setErrorMsg('Selecciona un método de pago para continuar.');
      return;
    }

    const { firstName, lastName } = splitName(name);
    const customer = {
      firstName,
      lastName,
      phone,
      email,
      documentId,
      address,
      city,
      department: department.trim() || 'Cundinamarca',
    };
    const notes =
      paymentMethod === 'cod'
        ? 'Pago contra entrega'
        : paymentMethod === 'epayco'
          ? 'Pago en línea ePayco'
          : paymentMethod === 'sistecredito'
            ? 'Financiamiento Sistecrédito (Compra a Cuotas)'
            : 'Pago por Nequi / transferencia';

    // Los pagos en línea (ePayco / Sistecrédito) NO se consideran firmes hasta
    // que la pasarela aprueba: por eso se difieren el correo, el Purchase y el
    // vaciado del carrito hasta la confirmación de pago.
    const deferredOnline = paymentMethod === 'epayco' || paymentMethod === 'sistecredito';

    setSubmitting(true);
    try {
      const created: CheckoutResult[] = [];
      const emails: Array<Promise<unknown>> = [];
      const customerName = `${firstName} ${lastName}`.trim();
      // Compra combinada (stock + producción): comparten un group_id para que el
      // pago se sincronice entre ambos pedidos sin depender de timestamps.
      const hasMixed = inStockItems.length > 0 && productionItems.length > 0;
      const groupId = hasMixed ? crypto.randomUUID() : undefined;
      const toItems = (list: ResolvedItem[]): OrderNotificationItem[] =>
        list.map((r) => ({
          productName: r.productName,
          sizeRange: r.sizeRange,
          quantity: r.quantity,
          unitPrice: r.unitPrice,
          subtotal: r.subtotal,
        }));
      if (inStockItems.length > 0) {
        const r = await createCheckoutOrder({
          customer,
          items: inStockItems.map((x) => ({ variantId: x.variantId, quantity: x.quantity })),
          paymentMethod,
          notes,
          groupId,
          chargeShipping: true,
        });
        created.push(r);
        // El correo de confirmación NO se envía al confirmar un pedido ePayco:
        // se dispara recién cuando el pago queda aprobado (ver confirmación).
        if (!deferredOnline) {
          emails.push(
            sendOrderConfirmation({
              orderNumber: r.orderNumber,
              customerEmail: email,
              customerName,
              customerPhone: phone,
              customerAddress: address,
              customerCity: city,
              total: r.total,
              paymentMethod,
              production: false,
              items: toItems(inStockItems),
            }),
          );
        }
      }
      if (productionItems.length > 0) {
        const r = await createProductionOrder({
          customer,
          items: productionItems.map((x) => ({ variantId: x.variantId, quantity: x.quantity })),
          paymentMethod,
          notes,
          groupId,
          chargeShipping: inStockItems.length === 0,
        });
        created.push(r);
        if (!deferredOnline) {
          emails.push(
            sendOrderConfirmation({
              orderNumber: r.orderNumber,
              customerEmail: email,
              customerName,
              customerPhone: phone,
              customerAddress: address,
              customerCity: city,
              total: r.total,
              paymentMethod,
              production: true,
              items: toItems(productionItems),
            }),
          );
        }
      }
      setResults(created);
      setConfirmedMethod(paymentMethod);
      // Purchase: en contra entrega / transferencia el pedido queda firme al
      // confirmarlo, así que el evento se dispara aquí (pixel + CAPI). Con
      // ePayco se dispara cuando el pago queda aprobado (página de confirmación).
      if (!deferredOnline && created.length > 0) {
        const totalPaid = created.reduce((s, r) => s + r.total, 0);
        const orderNumbers = created.map((r) => r.orderNumber);
        trackPurchase({
          eventId: orderNumbers.join('-'),
          value: totalPaid,
          currency: 'COP',
          numItems: count,
          contents: resolved.map((r) => ({
            id: r.productId,
            quantity: r.quantity,
            item_price: r.finalPrice,
          })),
          orderId: created[0]?.orderId,
          orderNumber: orderNumbers.join(', '),
          userData: {
            email,
            phone,
            firstName,
            lastName,
            city,
            department,
          },
        });
      }
      // Con ePayCo la orden queda registrada, pero el pago aún NO está confirmado.
      // Conservamos el carrito para que el cliente no pierda su selección si
      // retrocede o sale del pago; se vaciará recién cuando el pago se apruebe
      // (ver página de confirmación y el manejador de postMessage).
      if (!deferredOnline) {
        clearCart();
      }
      // Guarda los datos de entrega para autocompletar la próxima compra.
      saveDeliveryInfo({ name, phone, email, documentId, address, city, department });
      // Los correos se envían en paralelo sin bloquear la confirmación del pedido.
      void Promise.allSettled(emails);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'No se pudo completar el pedido.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePayNow() {
    const primary = payableOrder;
    if (!primary || !epaycoConfig) return;
    setOnlineLoading(true);
    setOnlineError('');
    const description = 'Compra en PCP · Paticas con Proposito';
    const payload: EpaycoPayPayload = {
      orderNumber: primary.orderNumber,
      orderId: primary.orderId,
      amount: payableTotal,
      description,
      customerName: name,
      customerEmail: email,
      customerPhone: phone,
      customerAddress: address,
      customerDocument: documentId,
    };
    // Recordamos el pago pendiente para poder retomarlo si el cliente sale de
    // ePayCo y regresa al checkout. Guardamos también qué productos forman este
    // pedido para vaciarlos del carrito únicamente cuando el pago se apruebe.
    savePendingEpayco({
      order: primary,
      orders: payableOrders ?? undefined,
      payload,
      orderedKeys: resolved.map((r) => `${r.productId}|${r.sizeId}`),
    });
    // Abre el checkout de ePayco embebido (modal onpage o página completa).
    const launchInline = async (display: 'standard' | 'onpage') => {
      if (!isEpaycoLoaded()) await loadEpaycoScript();
      openEpaycoCheckout({
        publicKey: epaycoConfig.publicKey,
        isTestMode: epaycoConfig.isTestMode,
        orderNumber: primary.orderNumber,
        orderId: primary.orderId,
        amount: payableTotal,
        description,
        customerName: name,
        customerEmail: email,
        customerPhone: phone,
        customerAddress: address,
        customerDocument: documentId,
        confirmationUrl: getEpaycoWebhookUrl(),
        responseUrl: getEpaycoResponseUrl(),
        display,
      });
    };

    try {
      if (epaycoConfig.checkoutDisplay === 'popup') {
        // Ventana externa: abre una pestaña con nuestra página de pago segura.
        const popup = window.open(getEpaycoPayPageUrl(), 'epayco_pay', 'width=480,height=820');
        if (!popup) {
          // El navegador bloqueó la ventana emergente: seguimos en esta pestaña.
          navigate('/checkout/epayco', { state: { payload } });
          return;
        }
        // Handshake: la pestaña nueva avisa cuando está lista para recibir datos.
        const onReady = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          const data = event.data as { type?: string } | null;
          if (data?.type === 'epayco-pay-ready') {
            popup.postMessage({ type: 'epayco-pay-data', payload }, window.location.origin);
            window.removeEventListener('message', onReady);
          }
        };
        window.addEventListener('message', onReady);
      } else if (epaycoConfig.checkoutDisplay === 'onpage') {
        // Modal flotante clásico (checkout.js).
        await launchInline('onpage');
      } else {
        // Modo ESTÁNDAR (página completa). Si por alguna razón falla, cae
        // automáticamente al modal flotante como respaldo.
        try {
          await launchInline('standard');
        } catch (stdErr) {
          console.warn('[epayco] el modo estándar falló, usando el modal:', stdErr);
          await launchInline('onpage');
        }
      }
    } catch (err) {
      setOnlineError(err instanceof Error ? err.message : 'No se pudo iniciar el pago.');
    } finally {
      setOnlineLoading(false);
    }
  }

  // Inicia la solicitud de financiamiento con Sistecrédito y abre su checkout
  // seguro (URL/token devuelto por la Edge Function del servidor).
  async function handleSistecredito() {
    const primary = payableOrder;
    if (!primary) return;
    setSistecreditoLoading(true);
    setSistecreditoError('');
    // Recordamos el intento pendiente en localStorage para poder RETOMAR este
    // mismo pedido si el cliente sale de Sistecrédito y vuelve al checkout (sin
    // crear un pedido duplicado). Guardamos también qué productos forman este
    // pedido para vaciarlos del carrito SOLO cuando el financiamiento se apruebe.
    savePendingSistecredito({
      order: primary,
      orders: payableOrders ?? undefined,
      amount: payableTotal,
      contact: phone || email || documentId,
      orderedKeys: resolved.map((r) => `${r.productId}|${r.sizeId}`),
    });
    try {
      const result = await startSistecreditoCheckout({
        orderId: primary.orderId,
        orderNumber: primary.orderNumber,
        amount: payableTotal,
        returnUrl: getSistecreditoReturnUrl(primary.orderNumber),
        customer: {
          name,
          document: documentId,
          phone,
          email,
          address,
          city,
          department: department.trim() || 'Cundinamarca',
        },
      });
      if (result.redirectUrl) {
        window.open(result.redirectUrl, '_blank', 'noopener,noreferrer');
      } else {
        setSistecreditoError(
          'Sistecrédito recibió tu solicitud, pero aún no devolvió la URL de su checkout. Estamos terminando el enrolamiento; escríbenos por WhatsApp para completar tu compra.',
        );
      }
    } catch (err) {
      setSistecreditoError(
        err instanceof Error ? err.message : 'No se pudo iniciar el financiamiento.',
      );
    } finally {
      setSistecreditoLoading(false);
    }
  }

  // Pantalla de confirmación
  if (results.length > 0 && confirmedMethod) {
    const grandTotal = results.reduce((s, r) => s + r.total, 0);
    const waMessage = `Hola PCP, acabo de realizar el pedido *${results
      .map((r) => r.orderNumber)
      .join(', ')}* en la tienda. Adjunto comprobante de pago.`;
    const waNumber = contactWhatsapp.replace(/\D/g, '');
    const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`;

    return (
      <div className="relative z-0 min-h-screen bg-background-50">
        <div className="paw-watermark absolute inset-0 -z-10 pointer-events-none opacity-[0.04]" aria-hidden="true" />
        <StoreNavbar />
        <div className="max-w-lg mx-auto px-4 py-12 md:py-16">
          <div className="bg-white rounded-2xl border border-background-200 p-6 md:p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <i className="ri-check-line text-emerald-600 text-3xl"></i>
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">
              ¡Pedido confirmado!
            </h1>
            <p className="text-foreground-600 text-sm mb-6">
              Tu pedido fue registrado con éxito.
            </p>

            <div className="bg-background-50 rounded-xl p-4 mb-6 text-left space-y-2">
              {results.map((r) => (
                <div key={r.orderId} className="flex justify-between gap-3 text-sm">
                  <span className="text-foreground-500 flex items-center gap-1.5 min-w-0">
                    <span className="whitespace-nowrap">{r.orderNumber}</span>
                    {r.status === 'para_produccion' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
                        Para producción
                      </span>
                    )}
                  </span>
                  <span className="font-bold text-foreground-950">${r.total.toLocaleString()}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm pt-2 border-t border-background-200">
                <span className="text-foreground-500">Total</span>
                <span className="font-bold text-accent-600">${grandTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-foreground-500">Método de pago</span>
                <span className="font-medium text-foreground-900">
                  {confirmedMethod === 'cod' ? 'Contra entrega' : confirmedMethod === 'epayco' ? 'ePayco / Tarjeta / PSE / Nequi' : confirmedMethod === 'sistecredito' ? 'Sistecrédito (Compra a Cuotas)' : 'Nequi / Transferencia'}
                </span>
              </div>
            </div>

            {results.some((r) => r.status === 'para_produccion') && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-left flex items-start gap-2">
                <i className="ri-tools-line text-amber-600 mt-0.5"></i>
                <p className="text-xs text-amber-800 leading-relaxed">
                  Uno de tus productos no está en stock. Lo mandaremos a <strong>producción</strong>{' '}
                  lo antes posible. Tiempo de entrega estimado en <strong>5-6 días hábiles</strong>.
                  Te estaremos avisando sobre el proceso de compra de tu producto en producción.
                </p>
              </div>
            )}

            {confirmedMethod === 'epayco' && payableOrder ? (
              <div className="space-y-4">
                {externalPaid ? (
                  <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-left flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-emerald-600 text-lg mt-0.5"></i>
                    <p className="text-sm text-emerald-800 leading-relaxed">
                      ¡Pago aprobado! Tu pedido <strong>{externalPaid}</strong> quedó pagado y en
                      preparación. Gracias por tu compra, Patyfamilia.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-foreground-600">
                      {epaycoConfig?.isTestMode
                        ? <>Estás en el <strong>entorno de pruebas</strong> de ePayco. Podrás pagar con tarjeta o PSE de prueba sin cargos reales.</>
                        : <>Tu pago será procesado de forma segura por <strong>ePayco</strong> (tarjeta, PSE o Nequi). Haz clic en el botón para completar el pago.</>}
                      {epaycoConfig?.checkoutDisplay === 'popup' && (
                        <> Se abrirá una <strong>ventana segura</strong> de ePayco; al terminar, el resultado aparecerá ahí mismo.</>
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={handlePayNow}
                      disabled={onlineLoading}
                      className="w-full flex items-center justify-center gap-2 py-3.5 bg-accent-500 text-white rounded-xl font-semibold text-sm hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {onlineLoading ? (
                        <><i className="ri-loader-4-line animate-spin mr-2"></i>Preparando pago...</>
                      ) : (
                        <><i className="ri-bank-card-line text-lg"></i>Pagar con ePayco</>
                      )}
                    </button>
                    {onlineError && (
                      <p className="text-xs text-red-600 text-left">{onlineError}</p>
                    )}
                  </>
                )}
                <div className="flex items-center gap-3">
                  <span className="flex-1 h-px bg-background-200"></span>
                  <span className="text-xs text-foreground-400">o como respaldo</span>
                  <span className="flex-1 h-px bg-background-200"></span>
                </div>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={() => track('Lead', { content_name: 'WhatsApp', currency: 'COP', value: 0 })}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-white border border-emerald-300 text-emerald-600 rounded-xl font-semibold text-sm hover:bg-emerald-50 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Enviar comprobante por WhatsApp
                </a>
              </div>
            ) : confirmedMethod === 'sistecredito' && payableOrder ? (
              <div className="space-y-4">
                {sistecreditoApproved ? (
                  <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-left flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-emerald-600 text-lg mt-0.5"></i>
                    <p className="text-sm text-emerald-800 leading-relaxed">
                      ¡Financiamiento aprobado! Tu pedido <strong>{sistecreditoApproved}</strong> quedó pagado y en
                      preparación. Gracias por tu compra, Patyfamilia.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-foreground-600">
                      Tu pedido <strong>{payableOrderLabel}</strong> quedó registrado. Completa la solicitud de
                      financiamiento con <strong>Sistecrédito</strong> para pagar en cuotas. Te llevaremos a su
                      checkout seguro.
                    </p>
                    <button
                      type="button"
                      onClick={handleSistecredito}
                      disabled={sistecreditoLoading}
                      className="w-full flex items-center justify-center gap-2 py-3.5 bg-accent-500 text-white rounded-xl font-semibold text-sm hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {sistecreditoLoading ? (
                        <><i className="ri-loader-4-line animate-spin mr-2"></i>Preparando...</>
                      ) : (
                        <><i className="ri-hand-coin-line text-lg"></i>Solicitar financiamiento con Sistecrédito</>
                      )}
                    </button>
                    {sistecreditoError && (
                      <p className="text-xs text-red-600 text-left">{sistecreditoError}</p>
                    )}
                  </>
                )}
                <div className="flex items-center gap-3">
                  <span className="flex-1 h-px bg-background-200"></span>
                  <span className="text-xs text-foreground-400">o como respaldo</span>
                  <span className="flex-1 h-px bg-background-200"></span>
                </div>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={() => track('Lead', { content_name: 'WhatsApp', currency: 'COP', value: 0 })}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-white border border-emerald-300 text-emerald-600 rounded-xl font-semibold text-sm hover:bg-emerald-50 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Enviar comprobante por WhatsApp
                </a>
              </div>
            ) : (
              <>
                {confirmedMethod === 'cod' ? (
                  <p className="text-sm text-foreground-600 mb-6">
                    Enviaremos tu pedido por <strong>Interrapidísimo</strong>. Pagas al recibir en la
                    puerta de tu casa.
                  </p>
                ) : (
                  <p className="text-sm text-foreground-600 mb-6">
                    Para confirmar tu pedido, envía el comprobante de pago por WhatsApp a nuestro
                    canal oficial.
                  </p>
                )}

                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={() => track('Lead', { content_name: 'WhatsApp', currency: 'COP', value: 0 })}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Enviar comprobante por WhatsApp
                </a>
                <p className="text-xs text-foreground-400 mt-3">{contactWhatsapp}</p>
              </>
            )}

            <SectionLink
              section="catalogo"
              onClick={() => {
                // Solo descartamos el pago pendiente si el pago ya fue aprobado.
                // Si aún no se ha pagado, lo conservamos para que el cliente
                // pueda retomarlo sin crear un pedido duplicado.
                if (externalPaid) clearPendingEpayco();
              }}
              className="block mt-4 text-sm text-accent-600 hover:text-accent-700 font-medium"
            >
              Seguir comprando
            </SectionLink>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-0 min-h-screen bg-background-50">
      <div className="paw-watermark absolute inset-0 -z-10 pointer-events-none opacity-[0.04]" aria-hidden="true" />
      <StoreNavbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <h1 className="text-xl md:text-3xl font-bold text-foreground-950 mb-6">Finalizar compra</h1>

        {items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-background-200 p-10 text-center">
            <i className="ri-shopping-bag-3-line text-5xl text-foreground-300 block mb-4"></i>
            <p className="text-foreground-600 mb-1">Tu carrito está vacío</p>
            <p className="text-sm text-foreground-500 mb-6">Agrega unas paticas con propósito.</p>
            <SectionLink
              section="catalogo"
              className="inline-block px-6 py-3 bg-accent-500 text-white rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              Ir al catálogo
            </SectionLink>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
            {/* Estado de carga / error de precios y stock */}
            {dataLoading && (
              <div className="lg:col-span-5 bg-white border border-background-200 rounded-2xl p-4 flex items-center gap-3">
                <i className="ri-loader-4-line animate-spin text-accent-600 text-lg"></i>
                <p className="text-sm text-foreground-600">Cargando precios y stock… un momento.</p>
              </div>
            )}
            {dataError && !dataLoading && (
              <div className="lg:col-span-5 bg-red-50 border border-red-200 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-red-600 text-lg mt-0.5"></i>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-red-700">No pudimos cargar los precios y el stock</p>
                    <p className="text-xs text-red-600 mt-0.5">
                      Revisa tu conexión e inténtalo de nuevo. No continúes con el pago sin ver el valor real de tu pedido.
                    </p>
                    <button
                      type="button"
                      onClick={retryData}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-refresh-line"></i> Reintentar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Aviso informativo obligatorio del checkout (solo si está activo) */}
            {notices.checkoutEnabled && (
              <div id="checkout-notice" className="lg:col-span-5 bg-amber-50 border border-amber-300 rounded-2xl p-4 md:p-5 scroll-mt-24">
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0">
                    <i className="ri-alert-line text-amber-600 text-lg"></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-semibold text-amber-900 mb-1.5">Información importante de tu pedido</h2>
                    <p className="text-xs md:text-sm text-amber-800 leading-relaxed whitespace-pre-line">{notices.checkoutText}</p>
                    <label className="mt-3 flex items-start gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={noticeAccepted}
                        onChange={(e) => setNoticeAccepted(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-amber-600"
                      />
                      <span className="text-xs md:text-sm font-medium text-amber-900">Entendido, continuar con el pago</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Columna izquierda: datos + pago */}
            <div className="lg:col-span-3 space-y-5">
              <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
                <h2 className="text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-map-pin-line text-accent-600"></i>Datos de entrega
                </h2>
                <p className="text-xs text-foreground-400 -mt-2 mb-4">
                  Los campos con <span className="text-red-500">*</span> son obligatorios. La dirección de entrega es obligatoria para despachar tu pedido.
                </p>
                {prefilled && (
                  <div className="mb-4 p-3 rounded-lg bg-primary-50 border border-primary-200 flex items-start gap-2">
                    <i className="ri-magic-line text-primary-700 mt-0.5"></i>
                    <p className="text-xs text-foreground-700 flex-1 leading-relaxed">
                      Cargamos tus datos del último pedido. <strong>Revísalos</strong> antes de confirmar.
                    </p>
                    <button
                      type="button"
                      onClick={clearDeliveryInfo}
                      className="text-xs font-medium text-accent-600 hover:text-accent-700 whitespace-nowrap cursor-pointer"
                    >
                      Limpiar
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="text-xs font-medium text-foreground-600 mb-1 block">Nombre completo <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Ej: María Pérez"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground-600 mb-1 block">Celular <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+57 300 000 0000"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground-600 mb-1 block">Correo electrónico</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="correo@ejemplo.com"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground-600 mb-1 block">Documento de identificación</label>
                    <input
                      type="text"
                      value={documentId}
                      onChange={(e) => setDocumentId(e.target.value)}
                      placeholder="Ej: 1018452100"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground-600 mb-1 block">Ciudad / Municipio <span className="text-red-500">*</span></label>
                    <select
                      value={COLOMBIA_CITIES.includes(city) ? city : city ? '__other__' : ''}
                      onChange={(e) => setCity(e.target.value === '__other__' ? '' : e.target.value)}
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    >
                      <option value="">Selecciona tu ciudad...</option>
                      {COLOMBIA_CITIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                      <option value="__other__">Otra ciudad / municipio</option>
                    </select>
                    {(city === '' || !COLOMBIA_CITIES.includes(city)) && (
                      <input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="Escribe tu ciudad o municipio"
                        className="w-full mt-2 text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                      />
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground-600 mb-1 block">Departamento <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      placeholder="Ej: Cundinamarca"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-medium text-foreground-600 mb-1 block">Dirección <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Ej: Calle 123 #45-67, Casa 5, Apto 302, Torre B, Manzana 4"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                    <p className="text-xs text-foreground-400 mt-1">Especifica casa, apartamento, torre o manzana para una entrega precisa.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
                <h2 className="text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-bank-card-line text-accent-600"></i>Método de pago
                </h2>
                <div className="space-y-2.5">
                  {enableCod && (
                    <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'cod' ? 'border-accent-500 bg-accent-50' : 'border-background-200 hover:border-background-300'}`}>
                      <input
                        type="radio"
                        name="payment"
                        checked={paymentMethod === 'cod'}
                        onChange={() => setPaymentMethod('cod')}
                        className="mt-1 accent-accent-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">Pago contra entrega</p>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          Paga en efectivo al recibir tu pedido. Sujeto a cobertura de Interrapidísimo.
                        </p>
                        {codSurchargePercent > 0 && cartChargesShipping && (
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2 flex items-start gap-1.5">
                            <i className="ri-information-line mt-0.5"></i>
                            <span>Este método tiene un recargo del {codSurchargePercent}% sobre el valor y el envío de las <strong>babuchas sencillas</strong>. Las <strong>babuchas premium</strong> tienen envío gratis y no pagan recargo.</span>
                          </p>
                        )}
                      </div>
                    </label>
                  )}
                  {enableTransfer && (
                    <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'transfer' ? 'border-accent-500 bg-accent-50' : 'border-background-200 hover:border-background-300'}`}>
                      <input
                        type="radio"
                        name="payment"
                        checked={paymentMethod === 'transfer'}
                        onChange={() => setPaymentMethod('transfer')}
                        className="mt-1 accent-accent-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">Nequi Negocios / Transferencia</p>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          Recibirás los datos de pago y envías tu comprobante por WhatsApp.
                        </p>
                      </div>
                    </label>
                  )}
                  {epaycoEnabled && (
                    <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'epayco' ? 'border-accent-500 bg-accent-50' : 'border-background-200 hover:border-background-300'}`}>
                      <input
                        type="radio"
                        name="payment"
                        checked={paymentMethod === 'epayco'}
                        onChange={() => setPaymentMethod('epayco')}
                        className="mt-1 accent-accent-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">ePayco · Tarjeta · PSE · Nequi</p>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          Paga en línea de forma segura con tarjeta, PSE o Nequi a través de ePayco.
                        </p>
                      </div>
                    </label>
                  )}
                  {sistecreditoEnabled && (
                    <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'sistecredito' ? 'border-accent-500 bg-accent-50' : 'border-background-200 hover:border-background-300'}`}>
                      <input
                        type="radio"
                        name="payment"
                        checked={paymentMethod === 'sistecredito'}
                        onChange={() => setPaymentMethod('sistecredito')}
                        className="mt-1 accent-accent-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">Sistecrédito · Compra a Cuotas</p>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          Financia tu compra y paga en cuotas cómodas con Sistecrédito.
                        </p>
                      </div>
                    </label>
                  )}
                  {!enableCod && !enableTransfer && !epaycoEnabled && !sistecreditoEnabled && (
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                      <div className="flex items-start gap-2">
                        <i className="ri-information-line mt-0.5"></i>
                        <span>No hay métodos de pago habilitados en este momento. Escríbenos por WhatsApp y completamos tu pedido manualmente.</span>
                      </div>
                      <a
                        href={`https://wa.me/${contactWhatsapp.replace(/\D/g, '')}?text=${encodeURIComponent('Hola PCP, quiero completar mi pedido pero no veo métodos de pago disponibles.')}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={() => track('Lead', { content_name: 'WhatsApp', currency: 'COP', value: 0 })}
                        className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 text-white rounded-lg font-semibold text-sm hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-whatsapp-line text-base"></i>
                        Completar por WhatsApp
                      </a>
                    </div>
                  )}
                  {(enableCod || enableTransfer || epaycoEnabled || sistecreditoEnabled) && paymentMethod === null && (
                    <p className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-information-line"></i>Selecciona un método de pago para habilitar la confirmación.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Columna derecha: resumen */}
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
                <h2 className="text-base font-semibold text-foreground-900 mb-4">
                  Resumen ({count} {count === 1 ? 'ítem' : 'ítems'})
                </h2>
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {resolved.map((r) => (
                    <div key={`${r.productId}-${r.sizeId}`} className="flex gap-3 pb-3 border-b border-background-100">
                      <div className="w-12 h-12 rounded-lg bg-background-100 flex-shrink-0 flex items-center justify-center">
                        <i className="ri-footprint-line text-foreground-400 text-lg"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate">
                          {r.productName}
                          {r.production && (
                            <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 align-middle whitespace-nowrap">
                              Producción
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-foreground-500">{r.sizeRange ? `Talla ${r.sizeRange}` : r.sizeName}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <button type="button" onClick={() => updateQuantity(r.productId, r.sizeId, r.quantity - 1)} className="w-6 h-6 rounded-md bg-background-100 text-foreground-600 hover:bg-background-200 flex items-center justify-center text-xs cursor-pointer">−</button>
                          <span className="text-sm font-medium text-foreground-900 w-5 text-center">{r.quantity}</span>
                          <button type="button" onClick={() => updateQuantity(r.productId, r.sizeId, r.quantity + 1)} className="w-6 h-6 rounded-md bg-background-100 text-foreground-600 hover:bg-background-200 flex items-center justify-center text-xs cursor-pointer">+</button>
                          <button
                            type="button"
                            onClick={() => setPendingRemove(r)}
                            className="ml-auto flex items-center gap-1 px-2 py-1 rounded-md text-xs text-foreground-500 border border-background-200 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-delete-bin-line"></i>
                            Eliminar
                          </button>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-foreground-950">${r.subtotal.toLocaleString()}</p>
                        <p className={`text-[10px] ${r.production ? 'text-amber-600' : r.variantId && r.available >= r.quantity ? 'text-emerald-600' : 'text-red-500'}`}>
                          {r.production ? 'Se manda a hacer' : r.variantId ? `${r.available} disp.` : 'no disponible'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-3 border-t border-background-200 space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-500">Valor de Productos</span>
                    <span className="font-medium text-foreground-900">${(total + discountTotal).toLocaleString()}</span>
                  </div>
                  {discountTotal > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">Descuento</span>
                      <span className="font-medium text-red-600">-${discountTotal.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-500">
                      Costo de Envío {shippingZone ? `· ${SHIPPING_ZONE_LABELS[shippingZone]}` : ''}
                    </span>
                    {!cartChargesShipping ? (
                      <span className="font-medium text-emerald-600">Incluido</span>
                    ) : shippingCost > 0 ? (
                      <span className="font-medium text-foreground-900">${shippingCost.toLocaleString()}</span>
                    ) : (
                      <span className="font-medium text-foreground-500">Selecciona tu ciudad</span>
                    )}
                  </div>
                  {applyCodSurcharge && (
                    <div className="flex justify-between text-sm gap-3">
                      <span className="text-foreground-500">Recargo Contraentrega · babuchas sencillas ({codSurchargePercent}%)</span>
                      <span className="font-medium text-foreground-900">${codSurcharge.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base pt-2 border-t border-background-100">
                    <span className="font-semibold text-foreground-950">Total a Pagar</span>
                    <span className="font-bold text-accent-600">${grandTotal.toLocaleString()}</span>
                  </div>
                </div>

                <div className="mt-3 p-3 rounded-lg bg-primary-500 border border-primary-600 flex items-start gap-2">
                  <i className="ri-gift-2-line text-foreground-950 text-base mt-0.5"></i>
                  <p className="text-xs text-foreground-950 leading-relaxed font-semibold">
                    Incluye <strong>Bandana de la Manada</strong> por tiempo limitado y{' '}
                    <strong>Bolsa Ecológica PCP</strong>
                  </p>
                </div>

                {productionItems.length > 0 && (
                  <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs flex items-start gap-2">
                    <i className="ri-tools-line mt-0.5"></i>
                    <span>Los productos agotados se mandan a hacer y entran a producción.</span>
                  </div>
                )}

                {!stockOk && (
                  <div className="mt-3 p-3 rounded-lg bg-red-50 text-red-600 text-xs flex items-start gap-2">
                    <i className="ri-error-warning-line"></i>
                    Alguno de los productos no tiene stock disponible. Ajusta las cantidades.
                  </div>
                )}
                {notices.checkoutEnabled && !noticeAccepted && (
                  <div className="mt-3 p-3.5 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-start gap-2">
                    <i className="ri-alert-line mt-0.5 flex-shrink-0 text-amber-600"></i>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold mb-0.5">Falta un paso para continuar</p>
                      <p className="leading-relaxed">
                        Para habilitar <strong>Confirmar pedido</strong> debes marcar la casilla{' '}
                        <strong>“Entendido, continuar con el pago”</strong> del aviso informativo del pedido.
                      </p>
                      <button
                        type="button"
                        onClick={() => document.getElementById('checkout-notice')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                        className="mt-2 inline-flex items-center gap-1 font-semibold text-amber-700 hover:text-amber-900 underline cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-arrow-up-line"></i>
                        Ir al aviso informativo
                      </button>
                    </div>
                  </div>
                )}
                {errorMsg && (
                  <div className="mt-3 p-3 rounded-lg bg-red-50 text-red-600 text-xs">{errorMsg}</div>
                )}

                <button
                  type="submit"
                  disabled={submitting || !formValid || !stockOk || !noticeOk || !dataReady}
                  className="w-full mt-4 py-3.5 bg-accent-500 text-white rounded-xl font-semibold text-sm hover:bg-primary-500 hover:text-foreground-950 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Procesando...
                    </>
                  ) : (
                    'Confirmar pedido'
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      {pendingRemove && (
        <Modal
          title="Eliminar producto"
          subtitle="Esta acción quita el producto de tu carrito."
          onClose={() => setPendingRemove(null)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setPendingRemove(null)}
                className="px-4 py-2 text-sm font-medium text-foreground-600 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  removeItem(pendingRemove.productId, pendingRemove.sizeId);
                  setPendingRemove(null);
                }}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Sí, eliminar
              </button>
            </>
          }
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-delete-bin-line text-red-500 text-lg"></i>
            </div>
            <p className="text-sm text-foreground-700 leading-relaxed">
              ¿Seguro que quieres eliminar <strong className="text-foreground-950">{pendingRemove.productName}</strong>
              {pendingRemove.sizeRange || pendingRemove.sizeName
                ? ` — ${pendingRemove.sizeRange ? `Talla ${pendingRemove.sizeRange}` : pendingRemove.sizeName}`
                : ''}{' '}
              de tu carrito?
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}